import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import TopBar from '../components/TopBar';
import { useAuth } from '../stores/auth';
import { buildTicket } from '../utils/escpos';
import {
  printerSupported,
  printerConnected,
  connectPrinter,
  printData,
  disconnectPrinter,
} from '../utils/bluetoothPrinter';

// ─── Tipos ───────────────────────────────────────────────────
type Acta = {
  id: number;
  acta_nro: string;
  patente: string;
  tipo_codigo: string | null;
  descripcion_snapshot: string | null;
  articulo_snapshot: string | null;
  monto_centavos: number;
  monto_pronto_pago_centavos: number | null;
  vence_pronto_pago_en: string | null;
  direccion: string | null;
  creado_en: string;
  municipio_id: number;
};

// ─── URL pública de pago ─────────────────────────────────────
// Siempre devuelve URL absoluta — el QR se escanea fuera de la app.
function buildPagarUrl(acta: Acta): string {
  const apiBase =
    (import.meta.env.VITE_API_BASE as string | undefined) ??
    (location.hostname === 'localhost'
      ? 'http://localhost/ciudadapp/api'
      : '/api');
  const publicBase = apiBase.replace(/\/api$/, '');
  const base = publicBase.startsWith('http')
    ? publicBase
    : location.origin + publicBase;
  return `${base}/pagar/infraccion.php?acta=${encodeURIComponent(acta.acta_nro)}&mid=${acta.municipio_id}`;
}

// ─── QR de pago ──────────────────────────────────────────────
function QRImage({ url }: { url: string }) {
  const [src, setSrc] = useState<string>('');
  useEffect(() => {
    QRCode.toDataURL(url, { width: 192, margin: 1, errorCorrectionLevel: 'M' })
      .then(setSrc)
      .catch(() => setSrc(''));
  }, [url]);
  if (!src) return <div className="w-48 h-48 mx-auto rounded-xl border border-gray-200 bg-gray-50 animate-pulse" />;
  return (
    <img
      src={src} alt="QR de pago" width={192} height={192}
      className="rounded-xl border border-gray-200 mx-auto block"
      style={{ imageRendering: 'pixelated' }}
    />
  );
}

// ─── Helpers ─────────────────────────────────────────────────
function pesos(cents: number): string {
  return '$' + (cents / 100).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function fmtFecha(s: string): string {
  return new Date(s.replace(' ', 'T')).toLocaleDateString('es-AR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

// ─── Componente ──────────────────────────────────────────────
export default function ConfirmarActa() {
  const nav        = useNavigate();
  const loc        = useLocation() as { state?: { acta?: Acta } };
  const acta       = loc.state?.acta;
  const inspector  = useAuth((s) => s.inspector);

  const [printing,         setPrinting]         = useState(false);
  const [printerErr,       setPrinterErr]        = useState<string | null>(null);
  const [btConnected,      setBtConnected]       = useState(false);
  const [printOk,          setPrintOk]           = useState(false);

  useEffect(() => {
    if (!acta) nav('/', { replace: true });
  }, [acta, nav]);

  // Sincronizar estado de conexión BT (por si se desconectó en background)
  useEffect(() => {
    const id = setInterval(() => {
      const connected = printerConnected();
      setBtConnected(connected);
      if (!connected) setPrintOk(false);
    }, 2000);
    return () => clearInterval(id);
  }, []);

  if (!acta) return null;

  const pagarUrl  = buildPagarUrl(acta);
  const ahora     = new Date();
  const ppVigente =
    acta.monto_pronto_pago_centavos !== null &&
    acta.vence_pronto_pago_en       !== null &&
    new Date(acta.vence_pronto_pago_en.replace(' ', 'T')) > ahora;
  const aCobrar = ppVigente ? acta.monto_pronto_pago_centavos! : acta.monto_centavos;

  async function handlePrint() {
    if (!acta) return;
    setPrinting(true);
    setPrinterErr(null);
    setPrintOk(false);
    try {
      if (!printerConnected()) {
        await connectPrinter();
        setBtConnected(true);
      }
      const ticket = await buildTicket({
        acta_nro:                   acta.acta_nro,
        patente:                    acta.patente,
        tipo_codigo:                acta.tipo_codigo,
        descripcion_snapshot:       acta.descripcion_snapshot,
        articulo_snapshot:          acta.articulo_snapshot,
        monto_centavos:             acta.monto_centavos,
        monto_pronto_pago_centavos: acta.monto_pronto_pago_centavos,
        vence_pronto_pago_en:       acta.vence_pronto_pago_en,
        direccion:                  acta.direccion,
        creado_en:                  acta.creado_en,
        inspector_nombre:           inspector ? `${inspector.nombre} ${inspector.apellido}` : 'Inspector',
        inspector_legajo:           inspector?.legajo ?? null,
        pagar_url:                  pagarUrl,
      });
      await printData(ticket);
      setPrintOk(true);
    } catch (e: unknown) {
      setPrinterErr(e instanceof Error ? e.message : 'Error al imprimir');
      setBtConnected(printerConnected());
    } finally {
      setPrinting(false);
    }
  }

  function handleDisconnect() {
    disconnectPrinter();
    setBtConnected(false);
    setPrintOk(false);
    setPrinterErr(null);
  }

  return (
    <>
      <TopBar title="Acta labrada" back />
      <main className="px-4 py-5 max-w-md mx-auto space-y-4 pb-24">

        {/* Encabezado éxito */}
        <div className="bg-green-50 border border-green-200 rounded-2xl p-4 text-center">
          <div className="text-4xl mb-2">✅</div>
          <p className="text-green-800 font-bold text-lg">Acta labrada</p>
          <p className="text-green-700 text-sm mt-1">
            N° <span className="font-mono font-bold">{acta.acta_nro}</span>
          </p>
        </div>

        {/* Datos del acta */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-0.5">Patente</p>
              <p className="text-2xl font-bold font-mono tracking-widest">{acta.patente}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-0.5">Fecha</p>
              <p className="text-sm font-medium">{fmtFecha(acta.creado_en)}</p>
            </div>
          </div>

          {acta.tipo_codigo && (
            <div>
              <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-0.5">Tipo</p>
              <p className="text-sm">{acta.tipo_codigo}{acta.articulo_snapshot ? ` · ${acta.articulo_snapshot}` : ''}</p>
            </div>
          )}

          {acta.descripcion_snapshot && (
            <div>
              <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-0.5">Descripción</p>
              <p className="text-sm">{acta.descripcion_snapshot}</p>
            </div>
          )}

          {acta.direccion && (
            <div>
              <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-0.5">Lugar</p>
              <p className="text-sm">{acta.direccion}</p>
            </div>
          )}

          <div className="pt-1 border-t border-gray-100">
            {ppVigente ? (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                <p className="text-blue-800 font-bold text-sm">
                  Pronto pago: {pesos(acta.monto_pronto_pago_centavos!)}
                </p>
                <p className="text-blue-600 text-xs mt-0.5">
                  Vigente hasta {fmtFecha(acta.vence_pronto_pago_en!)} ·
                  Monto completo: {pesos(acta.monto_centavos)}
                </p>
              </div>
            ) : (
              <div>
                <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-0.5">Monto</p>
                <p className="text-2xl font-bold">{pesos(aCobrar)}</p>
              </div>
            )}
          </div>
        </div>

        {/* QR de pago */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-3 text-center">
            Código QR para el ciudadano
          </p>
          <QRImage url={pagarUrl} />
        </div>

        {/* Impresora Bluetooth */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xl">🖨️</span>
            <p className="text-sm font-bold text-gray-800">Imprimir ticket</p>
            <span
              className="ml-auto text-xs font-semibold px-2 py-0.5 rounded-full"
              style={{
                backgroundColor: btConnected ? '#f0fdf4' : '#f8fafc',
                color:           btConnected ? '#16a34a' : '#94a3b8',
              }}
            >
              {btConnected ? '● Conectada' : '○ Sin conectar'}
            </span>
          </div>

          {!printerSupported() ? (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3">
              ⚠️ Bluetooth solo está disponible en Chrome para Android. Abrí esta app desde Chrome.
            </p>
          ) : (
            <>
              {printOk && (
                <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-xl p-2 mb-3 text-center font-semibold">
                  ✅ Ticket impreso correctamente
                </p>
              )}

              <button
                type="button"
                onClick={handlePrint}
                disabled={printing}
                className="w-full rounded-xl py-3.5 font-bold text-sm text-white disabled:opacity-50"
                style={{ backgroundColor: '#1e293b' }}
              >
                {printing
                  ? '⏳ Imprimiendo…'
                  : btConnected
                    ? '🖨️ Imprimir ticket'
                    : '🔵 Conectar e imprimir'}
              </button>

              {printerErr && (
                <p className="text-xs text-red-500 mt-2 leading-relaxed">{printerErr}</p>
              )}

              {btConnected && (
                <button
                  type="button"
                  onClick={handleDisconnect}
                  className="w-full text-xs text-gray-400 mt-2 py-1 underline"
                >
                  Desconectar impresora
                </button>
              )}

              <p className="text-xs text-gray-400 mt-3 leading-relaxed">
                La primera vez te pedirá que elijas la impresora de la lista de dispositivos Bluetooth.
                Las siguientes veces se conecta sola si ya está emparejada.
              </p>
            </>
          )}
        </div>

      </main>
    </>
  );
}
